import { useNavigate } from "react-router-dom";
import { totalPeopleCommitted, totalPeopleNeeded } from "../lib/domain";

// PastAttemptStack — shared, presentation-only "history-underneath"
// stack of small chip-cards, one per previous attempt of a project.
//
// Rules:
//   * Renders nothing when a project has no `previousAttemptId`.
//   * Walks the chain backwards from the current project until it
//     runs out or hits a cycle guard.
//   * Displays previous attempts in REVERSE CHRONOLOGICAL order
//     (attempt N-1 first, then N-2 …, oldest last). Matches the
//     "layers of history underneath the thing that exists now"
//     framing rather than a top-down timeline.
//   * Each chip is clickable and navigates to that attempt's
//     detail page using the existing routing (`/project/:id`).
//   * Visually subordinate: shorter, indented from the left with
//     a subtle vertical stem connecting them to the current card,
//     muted colour, smaller typography, minimal information density.
//   * Does NOT add to any parent-section project count — it's
//     history *belonging to* the current project card, not new items.
//
// Info shown per attempt (compact):
//   * "Attempt N" · outcome ("Didn't gather" for closed-not-met,
//     otherwise the effective status label)
//   * People committed / needed
//   * If present, `when` phrase (season/date period) as tertiary text
function PastAttemptChip({ project, pledges }) {
  const nav = useNavigate();
  const committed = totalPeopleCommitted(project, pledges);
  const needed = totalPeopleNeeded(project);
  // Outcome label. Most historicals are closed-not-met; support
  // completed too in case a chain member is a success prior to a
  // deliberate follow-up attempt.
  const outcome = project.status === "closed-not-met"
    ? "Didn't gather"
    : project.status === "completed"
      ? "Completed"
      : project.status === "happening"
        ? "Happening"
        : "In progress";
  return (
    <button
      type="button"
      className="c-past-attempt-chip"
      onClick={() => nav(`/project/${project.id}`)}
      data-testid={`past-attempt-chip-${project.id}`}
      aria-label={`Attempt ${project.attemptNumber} · ${outcome} · ${committed} of ${needed} committed`}
    >
      <span className="c-past-attempt-chip-stem" aria-hidden="true" />
      <span className="c-past-attempt-chip-body">
        <span className="c-past-attempt-chip-line1">
          <span className="c-past-attempt-chip-number">
            Attempt {project.attemptNumber}
          </span>
          <span className="c-past-attempt-chip-outcome">· {outcome}</span>
        </span>
        <span className="c-past-attempt-chip-line2">
          {committed} of {needed} committed
          {project.when ? <span className="c-past-attempt-chip-when"> · {project.when}</span> : null}
        </span>
      </span>
    </button>
  );
}

export function PastAttemptStack({ project, allProjects, pledges }) {
  if (!project?.previousAttemptId) return null;
  // Walk backwards through the chain with a cycle guard.
  const byId = new Map(allProjects.map((p) => [p.id, p]));
  const history = [];
  const guard = new Set([project.id]);
  let cur = byId.get(project.previousAttemptId);
  while (cur && !guard.has(cur.id)) {
    history.push(cur);
    guard.add(cur.id);
    cur = cur.previousAttemptId ? byId.get(cur.previousAttemptId) : null;
  }
  if (!history.length) return null;
  return (
    <div className="c-past-attempt-stack" data-testid={`past-attempt-stack-${project.id}`}>
      {history.map((h) => (
        <PastAttemptChip key={h.id} project={h} pledges={pledges} />
      ))}
    </div>
  );
}
